import React, { useEffect, useRef, useState } from "react";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const ISLAND_CLASS =
  "fixed left-4 top-4 z-[60] flex items-center gap-1 rounded-xl border border-border bg-surface/95 p-2 shadow-2xl backdrop-blur motion-safe:animate-workspace-in";
const MENU_BUTTON_CLASS =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-text hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary";
const TITLE_CHIP_CLASS =
  "flex h-9 items-center rounded-md px-2 font-montserrat text-sm font-semibold uppercase text-text hover:bg-surface-muted";
const TITLE_INPUT_CLASS =
  "h-9 w-40 rounded-md border border-border bg-surface-muted px-2 font-montserrat text-sm font-semibold uppercase text-text focus:outline-none focus:ring-2 focus:ring-primary";
const SAVE_DOT_BASE_CLASS = "h-2 w-2 shrink-0 rounded-full";
const SAVE_DOT_SAVING_CLASS = "bg-warning animate-pulse";
const SAVE_DOT_SAVED_CLASS = "bg-success";
const DROPDOWN_CLASS = "absolute left-0 top-full mt-2 w-56 rounded-xl border border-border bg-surface p-1.5 shadow-2xl";
const MENU_ITEM_CLASS = "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm text-text hover:bg-surface-muted";
const DANGER_MENU_ITEM_CLASS = "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm text-danger-text hover:bg-danger-surface";
const MENU_ITEM_LABEL_CLASS = "flex items-center gap-2";
const MENU_HINT_CLASS = "text-xs text-text-muted";
const EXPIRY_ROW_CLASS = "flex items-center justify-between rounded-md px-3 py-2 text-sm text-text";
const EXPIRY_SELECT_CLASS = "h-8 rounded border border-border bg-surface-muted text-sm text-text";
const DIVIDER_CLASS = "my-1 border-t border-border";
const ICON_CLASS = "h-4 w-4";
const TITLE_PLACEHOLDER = "Add a title";
const NOTE_MENU_LABEL = "Note menu";
const NOTE_TITLE_LABEL = "Note title";
const NOTE_EXPIRY_LABEL = "Note expiry";
const SAVING_LABEL = "Saving";
const SAVED_LABEL = "Saved";
const SAVE_LABEL = "Save";
const SAVE_HINT = "⌘S";
const VIEW_NOTE_LABEL = "View note";
const EXPIRY_LABEL = "Expiry";
const CLEAR_CANVAS_LABEL = "Clear canvas";
const DELETE_NOTE_LABEL = "Delete note";
const EXIT_FULL_SCREEN_LABEL = "Exit full screen";
const EXIT_HINT = "Esc";
/** Save-state indicator dot. @param {{ isSaving: boolean }} props */
const SaveStateDot = ({ isSaving }) => {
  const label = isSaving ? SAVING_LABEL : SAVED_LABEL;
  const stateClass = isSaving ? SAVE_DOT_SAVING_CLASS : SAVE_DOT_SAVED_CLASS;
  return (
    <span
      aria-label={label}
      className={[SAVE_DOT_BASE_CLASS, stateClass].join(" ")}
      role="status"
      title={label}
    />
  );
};

/** Inline-editable title chip. @param {{ onTitleChange: Function, title: string }} props */
const TitleChip = ({ onTitleChange, title }) => {
  const [isEditing, setIsEditing] = useState(false);
  const handleKeyDown = (event) => {
    if (event.key === "Escape") event.stopPropagation();
    if (event.key === "Enter" || event.key === "Escape") setIsEditing(false);
  };
  if (isEditing) {
    return (
      <input
        aria-label={NOTE_TITLE_LABEL}
        autoFocus
        className={TITLE_INPUT_CLASS}
        onBlur={() => setIsEditing(false)}
        onChange={(event) => onTitleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        type="text"
        value={title}
      />
    );
  }
  return (
    <button aria-label={NOTE_TITLE_LABEL} className={TITLE_CHIP_CLASS} onClick={() => setIsEditing(true)} type="button">
      {title || <span className="text-text-muted">{TITLE_PLACEHOLDER}</span>}
    </button>
  );
};

/** Single actionable dropdown row. @param {object} props */
const MenuItem = ({ Icon, hint, isDanger = false, label, onClick }) => (
  <button className={isDanger ? DANGER_MENU_ITEM_CLASS : MENU_ITEM_CLASS} onClick={onClick} role="menuitem" type="button">
    <span className={MENU_ITEM_LABEL_CLASS}>
      <Icon className={ICON_CLASS} fontSize="small" />
      {label}
    </span>
    {hint && <span className={MENU_HINT_CLASS}>{hint}</span>}
  </button>
);

/** Expiry label plus native select. @param {object} props */
const ExpiryRow = ({ expiry, expiryOptions, onExpiryChange }) => (
  <div className={EXPIRY_ROW_CLASS} role="none">
    <span className={MENU_ITEM_LABEL_CLASS}>
      <ScheduleOutlinedIcon className={ICON_CLASS} fontSize="small" />
      {EXPIRY_LABEL}
    </span>
    <select
      aria-label={NOTE_EXPIRY_LABEL}
      className={EXPIRY_SELECT_CLASS}
      onChange={(event) => onExpiryChange(event.target.value)}
      value={expiry}
    >
      {expiryOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

/** Note-actions menu anchored under the hamburger button. @param {object} props */
const DropdownMenu = ({ expiry, expiryOptions, onClear, onDelete, onExitFullscreen, onExpiryChange, onSave, onView }) => (
  <div className={DROPDOWN_CLASS} role="menu">
    <MenuItem Icon={SaveOutlinedIcon} hint={SAVE_HINT} label={SAVE_LABEL} onClick={onSave} />
    <MenuItem Icon={VisibilityOutlinedIcon} label={VIEW_NOTE_LABEL} onClick={onView} />
    <ExpiryRow expiry={expiry} expiryOptions={expiryOptions} onExpiryChange={onExpiryChange} />
    <div className={DIVIDER_CLASS} />
    <MenuItem Icon={DeleteSweepOutlinedIcon} label={CLEAR_CANVAS_LABEL} onClick={onClear} />
    <MenuItem Icon={DeleteOutlineOutlinedIcon} isDanger label={DELETE_NOTE_LABEL} onClick={onDelete} />
    <div className={DIVIDER_CLASS} />
    <MenuItem Icon={FullscreenExitOutlinedIcon} hint={EXIT_HINT} label={EXIT_FULL_SCREEN_LABEL} onClick={onExitFullscreen} />
  </div>
);
const useCloseOnOutsideInteraction = (rootRef, isOpen, closeMenu) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) closeMenu();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [closeMenu, isOpen, rootRef]);
};
/** Floating island bar surfacing note actions over a fullscreen canvas. @param {object} props */
const CanvasFullscreenBar = ({
  expiry,
  expiryOptions,
  isSaving,
  onClear,
  onDelete,
  onExitFullscreen,
  onExpiryChange,
  onSave,
  onTitleChange,
  onView,
  title,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const closeMenu = () => setIsMenuOpen(false);
  useCloseOnOutsideInteraction(rootRef, isMenuOpen, closeMenu);
  const withClose = (action) => () => {
    action();
    closeMenu();
  };
  const handleMenuKeyDown = (event) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    closeMenu();
  };
  return (
    <div aria-label="Canvas note actions" className={ISLAND_CLASS} ref={rootRef}>
      <button
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-label={NOTE_MENU_LABEL}
        className={MENU_BUTTON_CLASS}
        onClick={() => setIsMenuOpen((previous) => !previous)}
        type="button"
      >
        <MenuOutlinedIcon fontSize="small" />
      </button>
      <TitleChip onTitleChange={onTitleChange} title={title} />
      <SaveStateDot isSaving={isSaving} />
      {isMenuOpen && (
        <div onKeyDown={handleMenuKeyDown}>
          <DropdownMenu
            expiry={expiry}
            expiryOptions={expiryOptions}
            onClear={withClose(onClear)}
            onDelete={withClose(onDelete)}
            onExitFullscreen={withClose(onExitFullscreen)}
            onExpiryChange={onExpiryChange}
            onSave={withClose(onSave)}
            onView={withClose(onView)}
          />
        </div>
      )}
    </div>
  );
};

export default CanvasFullscreenBar;
